import { NEAR_RPC_URLS } from "../config";

const DEFAULT_TIMEOUT_MS = 5000;

export class NearRpcError extends Error {
  constructor(kind, message, details = {}) {
    super(message);
    this.name = "NearRpcError";
    this.kind = kind;
    this.provider = details.provider || null;
    this.status = details.status ?? null;
    this.rpcCode = details.rpcCode ?? null;
    this.transient = Boolean(details.transient);
    if (details.cause) this.cause = details.cause;
  }
}

export const normalizeRpcUrl = (url) => String(url || "").replace(/\/+$/, "");

export const isKnownNearRpcUrl = (url) => {
  const normalized = normalizeRpcUrl(url);
  return NEAR_RPC_URLS.some((provider) => normalized === normalizeRpcUrl(provider));
};

export const isTransientHttpStatus = (status) =>
  status === 408 || status === 425 || status === 429 || status >= 500;

export const isTransientJsonRpcError = (error) => {
  if (!error || typeof error !== "object") return false;
  if (Number(error.code) === -429) return true;
  const text = [error.message, error.data?.error_message, error.data]
    .filter((value) => typeof value === "string")
    .join(" ")
    .toLowerCase();
  return /rate limit|too many requests|temporar(?:y|ily)|service unavailable|gateway|timeout|timed out|try again/.test(text);
};

export async function inspectRpcResponse(response) {
  if (!response?.ok) {
    return {
      transient: isTransientHttpStatus(Number(response?.status || 0)),
      kind: "HTTP_ERROR",
      status: Number(response?.status || 0),
      body: null,
    };
  }

  let text;
  try {
    text = await response.clone().text();
  } catch (cause) {
    return { transient: true, kind: "BODY_READ_ERROR", cause, body: null };
  }
  if (!text) return { transient: true, kind: "EMPTY_BODY", body: null };

  let body;
  try {
    body = JSON.parse(text);
  } catch (cause) {
    return { transient: true, kind: "MALFORMED_JSON", cause, body: null };
  }

  if (body?.error) {
    return {
      transient: isTransientJsonRpcError(body.error),
      kind: "RPC_ERROR",
      rpcCode: body.error.code ?? null,
      body,
    };
  }
  if (!Object.prototype.hasOwnProperty.call(body || {}, "result")) {
    return { transient: true, kind: "EMPTY_RESULT", body };
  }
  return { transient: false, kind: "OK", body };
}

function buildRpcPayload(contractId, methodName, argsBase64) {
  return {
    jsonrpc: "2.0",
    id: "nuvelor-wallet",
    method: "query",
    params: {
      request_type: "call_function",
      account_id: contractId,
      method_name: methodName,
      args_base64: argsBase64,
      finality: "final",
    },
  };
}

async function fetchWithTimeout(fetchImpl, provider, payload, timeoutMs) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetchImpl(provider, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timer);
  }
}

export async function viewNearFunction({
  contractId,
  methodName,
  argsBase64,
  providers = NEAR_RPC_URLS,
  fetchImpl = globalThis.fetch?.bind(globalThis),
  timeoutMs = DEFAULT_TIMEOUT_MS,
} = {}) {
  if (!fetchImpl) {
    throw new NearRpcError("NETWORK_ERROR", "NEAR RPC is unavailable", { transient: true });
  }
  const orderedProviders = Array.from(new Set((providers || []).map(normalizeRpcUrl).filter(Boolean)));
  if (!orderedProviders.length) {
    throw new NearRpcError("NO_PROVIDERS", "NEAR RPC is unavailable", { transient: true });
  }

  const payload = buildRpcPayload(contractId, methodName, argsBase64);
  let lastFailure = null;

  for (const provider of orderedProviders) {
    let response;
    try {
      response = await fetchWithTimeout(fetchImpl, provider, payload, timeoutMs);
    } catch (cause) {
      lastFailure = new NearRpcError(
        cause?.name === "AbortError" ? "TIMEOUT" : "NETWORK_ERROR",
        "NEAR RPC request failed",
        { provider, transient: true, cause }
      );
      continue;
    }

    const inspected = await inspectRpcResponse(response);
    if (inspected.kind === "OK") return inspected.body.result;

    if (!inspected.transient) {
      throw new NearRpcError(inspected.kind, "NEAR RPC request failed", {
        provider,
        status: inspected.status,
        rpcCode: inspected.rpcCode,
        transient: false,
      });
    }

    lastFailure = new NearRpcError(inspected.kind, "NEAR RPC request failed", {
      provider,
      status: inspected.status,
      rpcCode: inspected.rpcCode,
      transient: true,
      cause: inspected.cause,
    });
  }

  throw new NearRpcError("RPC_UNAVAILABLE", "NEAR RPC temporarily unavailable", {
    provider: lastFailure?.provider,
    status: lastFailure?.status,
    rpcCode: lastFailure?.rpcCode,
    transient: true,
    cause: lastFailure,
  });
}
